import React, { Component } from 'react';
import styled, { css } from 'styled-components';
import { connectDeep } from 'redux-utils';
import App from 'models/app';
import ContentGrid from 'components/v2/AppContentGrid/ContentGrid';
import ItemGrid from 'components/v2/AppContentGrid/ItemGrid';
import HomeContainer from 'components/v2/HomeContainer';
import { Transition } from 'components/v2/Transition';
import { ScrollContainer } from 'components/v2/ScrollContainer';
import i18n from 'i18n';
import { push, back } from 'redux-first-router';
import { setFocusedClock } from 'actions/gallery';
import { Portal } from '../../components/v2/Portal/index';
import ClockDetails from 'components/v2/MyApps/ClockDetails';
import { ButtonLink } from '../../components/v2/Button/index';
import AppShapedImage from '../../components/v2/AppShapedImage/index';
import { roundEdgeDevices, RoutePaths } from '../../constants';

const StyledAppShapedImage = styled(AppShapedImage)`
  margin-bottom: 12px;
`;

const Container = styled.div`
  height: calc(100vh - 64px);
  overflow-y: auto;
  background: var(--sys-color--surface);
  display: flex;
  flex-direction: column;
`;


const BottomFixedBar = styled.div`
  margin-top: auto;
  width: 100%;
  box-sizing: border-box;
  padding: 16px 32px;
  background: var(--sys-color--surface);
  display: flex;
  justify-content: center;

  @media (min-width: 321px) {
    padding: 16px;
  }
`;

const StyledLinkContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  text-decoration: none;

  a {
    width: 100%;
  }
`;

const mapStateToProps = (state: any) => {
  const { registry, gallery } = state;
  const { focusedClock } = gallery;
  const {
    installed: { loading: installedLoading, error, entries = [] },
    features: { flags, loading: featuresLoading },
    favorites = [],
  } = registry;


  const device = registry.currentUser?.device;
  const deviceName = device?.productName || null;
  const maxClocks = device?.maxFavoriteClocks || null;

  const favoriteClocksEnabled = !!(flags?.enableFavoriteClockfaces && maxClocks && maxClocks > 0);

  const installedClocks = [];
  if (favoriteClocksEnabled) {
    for (const appId of favorites) {
      const app = App.selectFromState(appId, state);
      if (app && app.isClock) {
        installedClocks.push(app);
      }
    }
  } else {
    for (const { appId } of entries) {
      const app = App.selectFromState(appId, state);
      if (app && app.isInstalledOrInstalling && app.isClock) {
        installedClocks.push(app);
      }
    }
  }

  let activeClock: any = null;
  if (installedClocks.length === 1) {
    activeClock = installedClocks[0];
  } else if (installedClocks.length > 1) {
    activeClock = installedClocks.find(({ isSelected }) => isSelected) ||
                  installedClocks.find(({ active }) => active) ||
                  null;
  }

  if (activeClock) {
    const activeIndex = installedClocks.findIndex(({ id }) => id === activeClock.id);
    if (activeIndex > 0) {
      installedClocks.splice(activeIndex, 1);
      installedClocks.unshift(activeClock);
    }
  }

  return {
    loading: (installedLoading && entries.length === 0) || featuresLoading,
    error,
    installedClocks,
    activeClockId: activeClock ? activeClock.id : null,
    deviceName,
    maxClocks,
    focusedClock,
    favoriteClocksEnabled,
  };
};

type ReduxProps = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

interface InstalledClocksPageProps extends ReduxProps { }

class InstalledClocksPage extends Component<InstalledClocksPageProps> {
  componentDidMount() {
    // Safe registration using Event Listener to prevent global namespace pollution
    window.addEventListener('hashchange', this.handleHashChange);
  }

  componentWillUnmount() {
    // Clean up listener on unmount
    window.removeEventListener('hashchange', this.handleHashChange);
  }

  private handleHashChange = () => {
    const { focusedClock, setFocusedClock } = this.props;
    const { hash } = window.location;

    if (focusedClock && hash === '#/v2/clocks/gallery') {
      setFocusedClock(null);
    }
  };

  private get focusedClock() {
    const { installedClocks, focusedClock } = this.props;
    return installedClocks.find(({ id }: any) => id === focusedClock);
  }

  renderBody() {
    const { installedClocks, deviceName, maxClocks, activeClockId, favoriteClocksEnabled } = this.props;
    const firstClock = installedClocks[0];


    return (
      <ScrollContainer scrollKey="installedClocksPage" navbarHeight={64}>
        <Container>

          <ContentGrid>
            {installedClocks.map((app: any) => (
              <ItemGrid
                key={app.id}
                id={app.id}
                title={app.name}
                linkTo="/v2/clocks/gallery?focused=true"
                onClick={() => this.props.setFocusedClock(app.id)}
                photo={
                  <StyledAppShapedImage
                    photoProperties={{
                      src: app.banners?.[0]?.uri || '',
                      alt: app.name,
                      width: app.banners?.[0]?.width,
                      height: app.banners?.[0]?.height,
                      isSquircle: roundEdgeDevices.includes(deviceName?.toLowerCase()),
                    }}
                    width={110}
                    height={110}
                    borderRadius="16px"
                    active={app.id === activeClockId}
                    activeBorderColor="var(--sys-color--primary)"
                    borderReservedSpace={true}
                  />
                }
              />
            ))}
          </ContentGrid>
          <Portal
            id='clock-details-screen'
            aria-label='clock-details-screen'
            content={
              <ClockDetails
                show={!!this.focusedClock}
                clock={this.focusedClock}
                deviceName={deviceName}
                favoriteClocksEnabled={favoriteClocksEnabled}
                favoriteClocksCount={installedClocks.length}
                installedClocksCount={installedClocks.length}
                maxFavoriteClocks={maxClocks}
                setFocusedClock={this.props.setFocusedClock}
                isFirstClock={firstClock?.id === this.focusedClock?.id}
              />
            }
          />
          <BottomFixedBar>
            <StyledLinkContainer>
              <ButtonLink to="/v2/clocks/discover" pageDirection="FORWARD" variant="filled" fullWidth>
                {i18n('page.ClockGallery.listPageName')()}
              </ButtonLink>
            </StyledLinkContainer>
          </BottomFixedBar>
        </Container>
      </ScrollContainer>
    );
  }

  render() {
    const { loading, error } = this.props;

    return (
      <HomeContainer selected={RoutePaths.GALLERY_CLOCKS} centerTitle="Watch faces">
        {loading || error ? (
          <Transition loading={loading} error={error} headerHeight={64} />
        ) : (
          this.renderBody()
        )}
      </HomeContainer>
    );
  }
}

const mapDispatchToProps = (dispatch: any) => ({
  setFocusedClock(clockId: string | null) {
    dispatch(setFocusedClock(clockId));
    clockId ? push('/v2/clocks/gallery?focused=true') : back();
  },
});

export default (connectDeep as any)(mapStateToProps, mapDispatchToProps)(InstalledClocksPage);

